import * as angular from 'angular';
import * as moment from 'moment';
import 'signalr';

import { IApplicationService } from '../../../services/data';
import { IAuthenticationService, ILoggerService, ISignalrService, IConfigurationService } from '../../../services/general';
import { notificationReceived, globalTaskChannel, Helper } from '../../../common';
import { Notification, NotificationType, ITaskUpdate, AppOverviewDto, ComponentDefinition } from '../../../models';
import { IStoreService } from '../../../services';
import { Store, StoreElement } from '../../../stateManagement';

class ApplicationsOverviewController implements angular.IComponentController {
    static controllerId: string = 'ApplicationsOverview';

    isLoading: boolean;
    applications: { [guid: string]: any };
    displayed: { [guid: string]: any };
    overviews: { [guid: string]: AppOverviewDto };
    searchApplication: string;
    taskCount: number;
    taskCancelledCount: number;

    readonly unknown: string;

    private _proxy: SignalR.Hub.Proxy;
    private _handler: (report: ITaskUpdate) => void;
    private _userName: string;
    private _unsubscribe: (() => void)[] = [];

    static $inject = [
        '$scope',
        '$rootScope',
        '$state',
        '$translate',
        'storeService',
        'applicationService',
        'configurationService',
        'signalrService',
        'loggerService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: angular.IRootScopeService,
        private _$state: angular.ui.IStateService,
        private _$translate: angular.translate.ITranslateService,
        private _storeService: IStoreService,
        private _applicationService: IApplicationService,
        private _configurationService: IConfigurationService,
        private _signalrService: ISignalrService,
        private _loggerService: ILoggerService
    ) {
        this.unknown = this._$translate.instant('Scheduled task must be restarted to have a name');
        this._configurationService.loadData();
        this.initializeSignalr();
    }

    $onInit() {
        this._storeService.register(
            store => this.refreshStore(store),
            [StoreElement.Authentication]
        );

        this._$scope.$watch(() => this.searchApplication, () => this.refreshDisplay());

        this._unsubscribe.push(this._$rootScope.$on(notificationReceived, (e, notification: Notification) => {
            if (notification && notification.type === NotificationType.TaskCompleted) {
                this.refresh();
            }
        }));
    }

    $onDestroy() {
        this._unsubscribe.forEach(u => u());
    }

    private refreshStore(store: Store): void {
        this._userName = store.authentication.value.userName;
        this.refresh();
    }

    private initializeSignalr(): void {
        const self = this;
        const proxy = this._signalrService.connection.createHubProxy('taskHub');
        this._signalrService.connect(() => {
            this._handler = (report: ITaskUpdate) => {
                self.refreshTask(report);
            };
            proxy.on('push', this._handler);
            this._$scope.$on('$destroy', () => this._proxy.off('push', this._handler));
            this._proxy = proxy;
            proxy.invoke('joinWorkEnvironment', globalTaskChannel);
        });
    }

    private refreshTask(report: ITaskUpdate): void {
        Object.keys(this.overviews).forEach(k => {
            const ov = this.overviews[k];
            if (ov.taskCurrent && ov.taskCurrent.guid === report.TaskGuid) {
                ov.taskProgress = report.Progress;
            }
        });
    }

    refresh() {
        this.isLoading = true;
        const result = {};
        this.taskCount = 0;
        this.taskCancelledCount = 0;

        this._applicationService.getForUser(this._userName, (data, status) => {
            if (status === 0) {
                data.forEach(d => result[d.application.guid] = { name: d.application.name });
                this.applications = result;
                this.refreshDisplay();
            } else {
                this._loggerService.logError('Cannot retrieve the applications list.');
            }
        });

        this._applicationService.getOverviews().then(data => {

            Object.keys(data).forEach(k => {
                const job = data[k].jobCurrent;
                if (job) {
                    const dateResult = moment(job.lastResult);
                    job.hasResult = dateResult.year() > 1;
                    job.lastResultSince = dateResult.fromNow();
                    job.startDateSince = moment(job.startDate).fromNow();
                    if (!job.name && data[k].jobNumber) {
                        //job.name = `(${this._unknown})`;
                        (job as any).hasNoName = true;
                    }
                }

                this.taskCount += data[k].taskActiveNumber;
                this.taskCancelledCount += data[k].taskCancelledNumber;
            });

            this.overviews = data;
        }).finally(() => {
            this.isLoading = false;
        });
    }

    refreshDisplay() {
        const result = {};

        if (this.applications) {
            const regExp = Helper.getRegExpCase(this.searchApplication, true);

            Object.keys(this.applications).forEach(k => {
                const app = this.applications[k];
                if (!app.name || !this.searchApplication || (regExp && app.name.match(regExp))) {
                    result[k] = app;
                }
            });
        }

        this.displayed = result;
    }

    connect(guid: string, name: string): void {
        this._storeService.dispatchers().authentication
            .connectByApplicationName(name, guid)
            .then(() => {
                this._$state.go('jobsDefinition');
            });
    }

    cancelAllTasks(guid?: string): void {
        this.isLoading = true;
        this._applicationService.cancelAllTasks(guid)
            .catch(reason => {
                this._loggerService.logError(reason);
            }).finally(() => {
                this.refresh();
            });
    }

    resumeAllTasks(guid?: string): void {
        this.isLoading = true;
        this._applicationService.resumeAllTasks(guid)
            .catch(reason => {
                this._loggerService.logError(reason);
            }).finally(() => {
                this.refresh();
            });
    }
}

export const wtApplicationsOverview: ComponentDefinition = {
    componentId: 'wtApplicationsOverview',
    controller: ApplicationsOverviewController,
    template: require('./applicationsOverview.component.html'),
    controllerAs: 'vm'
};
